import { BaseColumnMappingTransformation } from 'app/model/dataflow/base-column-mapping-transformation';
import { DataFlowExpressionUtils } from '../../expression.util';
import { Owned, Metamodel } from 'shell-models-lib';
import { DataFlowColumn } from 'app/model/dataset/dataflowcolumn';
import { DataFlowMapColumn } from 'app/model/dataflow/dataflow-map.column';
import { ColumnReference } from 'app/model/dataset/columnReference';
import { ColumnMappingBaseTransformPlainObject, ColumnMappingPlainObject, RuleBasedMappingPlainObject } from 'worker/app-workers/shared/plainObjects/columnMappingBase.plainObject';
import { BooleanDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { ComplexTypeUtils } from 'app/authoring/component/transformations/complex-type-utils';

export class ColumnMappingConvertor {
    public static convertFromPlainObject(sinkObject: ColumnMappingBaseTransformPlainObject, transformation: BaseColumnMappingTransformation,
            handledConfigs: string[]): BaseColumnMappingTransformation {
        let expression = sinkObject.configs.get('skipDuplicateMapOutputs') || sinkObject.configs.get('skipDuplicateMappings');
        transformation.skipDuplicateMapOutputs = DataFlowExpressionUtils.unwrapExpressionConfigValue(expression ? expression : 'false', transformation, BooleanDataFlowType.TYPE);

        expression = sinkObject.configs.get('skipDuplicateMapInputs');
        transformation.skipDuplicateMapInputs = DataFlowExpressionUtils.unwrapExpressionConfigValue(expression ? expression : 'false', transformation, BooleanDataFlowType.TYPE);
        handledConfigs.push(...['skipDuplicateMapOutputs', 'skipDuplicateMappings', 'skipDuplicateMapInputs']);

        sinkObject.mappings.forEach((mapping: ColumnMappingPlainObject) => {
            ColumnMappingConvertor._addColumnPlaceholder(mapping.alias, mapping.name, transformation, mapping.isField, mapping.isArraySelector, mapping.rowIndex);
        });

        sinkObject.rules.forEach((rule: RuleBasedMappingPlainObject) => {
            const ruleBasedMapping = transformation.addRuleBasedMapColumn();
            ruleBasedMapping.rowIndex = rule.rowIndex;
            ruleBasedMapping.deepHierarchy = rule.deepHierarchy;

            if (rule.unEscapedRegexString) {
                ruleBasedMapping.regexString = DataFlowExpressionUtils.unescapeSingleQuoteInDslString(rule.unEscapedRegexString).slice(1, -1); // The quotes have to be removed
            }
            if (rule.hierarchyLevelExpression) {
                if (rule.hierarchyLevelExpression[0] === '(') {
                    ruleBasedMapping.hierarchyLevelExpression = DataFlowExpressionUtils.unwrapExpressionConfigValue(rule.hierarchyLevelExpression, transformation);
                } else {
                    ruleBasedMapping.hierarchyLevelExpression.expression = DataFlowExpressionUtils.unwrapExpressionWithEmptyValue(rule.hierarchyLevelExpression);
                }
            }
            ruleBasedMapping.templateCondition.expression = DataFlowExpressionUtils.unwrapExpressionWithEmptyValue(rule.templateCondition);
            ruleBasedMapping.dataFlowExpression.expression = DataFlowExpressionUtils.unwrapExpressionWithEmptyValue(rule.columnNameExpression);
        });

        return transformation;
    }

    private static _addColumnPlaceholder(aliasName: string, actualColumnName: string, transformation: BaseColumnMappingTransformation, isField: boolean, isArraySelector: boolean, rowIndex: number): void {
        const fields = ComplexTypeUtils.splitComplexFieldPath(actualColumnName);
        const isComplexFieldPath = fields.length > 1 && isField;
        const additionalColumnPlaceholder = Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), transformation);
        additionalColumnPlaceholder.name = actualColumnName;
        const placeHolderColumnRef = new ColumnReference(additionalColumnPlaceholder);
        if (isComplexFieldPath) {
            placeHolderColumnRef.complexFieldPath = actualColumnName;
            additionalColumnPlaceholder.name = fields[0];
        }
        if (isArraySelector && additionalColumnPlaceholder.name && additionalColumnPlaceholder.name.endsWith(']')) {
            additionalColumnPlaceholder.name = additionalColumnPlaceholder.name.substr(0, additionalColumnPlaceholder.name.length - 3);
        }
        const sinkColumn = Owned.create<DataFlowMapColumn>(DataFlowMapColumn.MODEL, transformation);
        sinkColumn.columnRef = placeHolderColumnRef;
        sinkColumn.writeColumn = true;
        sinkColumn.name = aliasName;
        sinkColumn.rowIndex = rowIndex;
        transformation.schema.push(sinkColumn);
        sinkColumn.outputAlias = aliasName;
    }
}
